import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle, Notepad, Scales, WarningDiamond } from '@phosphor-icons/react'
import { useAuth } from '@/auth/context'
import { canOverrideTwoPerson } from '@/auth/roles'
import { useFormat } from '@/i18n'
import { cn } from '@/lib/cn'
import { Banner, Button, EmptyState, Field, Input, Modal, Select, TextArea } from '@/ui'
import { useLot, usePaperReview, useReviewPaperSlip, useSessions, type PaperSlip } from '../api/gate'
import { ActionButton } from '../components/Can'
import { CorrectionDialog } from '../components/CorrectionDialog'
import { HoldActions } from '../components/HoldActions'
import { ConsolePage, QueryState } from '../components/Layout'
import { PhotoViewer } from '../components/PhotoViewer'
import { Check, Kg, Section, Status, Stream, UserName, When } from '../components/bits'
import { exceptionLabel, humanize } from '../labels'
import { useUrlState } from '../lib/useUrlState'

const MISMATCH_FIELDS = ['farmer', 'crop', 'home_storage', 'bag_count', 'tag', 'sack_type', 'gross_weight', 'seal', 'moisture', 'sample_weight', 'other']

/** Paper-day review (F4-B3.c): the slip photo beside what was typed, one slip at a time. */
export function PaperReviewPage() {
  const [f, setF] = useUrlState({ sessionId: '', slip: '' })
  const days = useSessions({ captureMode: 'PAPER', page: 1, limit: 100 })
  const review = usePaperReview(f.sessionId || undefined)
  const slips = review.data?.slips ?? []
  const current = slips.find((s) => `${s.bookId}:${s.slipNo}` === f.slip) ?? slips.find((s) => !s.review.result && s.unitId) ?? slips[0]
  const pending = slips.filter((s) => s.unitId && !s.review.result).length

  return (
    <ConsolePage
      eyebrow="Farm gate"
      title="Paper-day review"
      description="When a phone could not be used, the day was written on receipt slips and typed in later. A supervisor who neither ran nor typed the day checks every slip against its photo."
      actions={
        <div className="w-72">
          <Select
            aria-label="Paper day"
            value={f.sessionId}
            onChange={(sessionId) => setF({ sessionId, slip: '' })}
            placeholder={days.isLoading ? 'Loading…' : 'Choose a paper day'}
            options={(days.data?.rows ?? []).map((d) => ({
              value: d.id,
              label: `${d.code} · ${d.villageName ?? ''} · ${d.status.toLowerCase()}`,
            }))}
          />
        </div>
      }
    >
      {!f.sessionId ? (
        <EmptyState icon={<Notepad />} title="Choose a paper day" description="Paper days are listed newest first. Days with slips still to review show in the day list too." />
      ) : (
        <QueryState query={review} label="the slips">
          {slips.length === 0 ? (
            <EmptyState icon={<Notepad />} title="No slips typed for this day yet" />
          ) : (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
              <nav aria-label="Slips" className="rounded-card border border-rule bg-surface">
                <div className="border-b border-rule px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-label text-muted">
                  {slips.length} slips · {pending} to review
                </div>
                <ul className="max-h-[70vh] divide-y divide-rule/70 overflow-y-auto">
                  {slips.map((s) => {
                    const key = `${s.bookId}:${s.slipNo}`
                    const selected = current && `${current.bookId}:${current.slipNo}` === key
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          aria-current={selected || undefined}
                          onClick={() => setF({ slip: key })}
                          className={cn('flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-paper-tint/60', selected && 'bg-paper-tint')}
                        >
                          <span className="min-w-0">
                            <span className="block font-mono text-[13px]">
                              {s.bookNo} · {s.slipNo}
                            </span>
                            <span className="block truncate text-[12.5px] text-muted">{s.typed.lots[0]?.farmerName ?? humanize(s.status)}</span>
                          </span>
                          <span className="flex shrink-0 flex-col items-end gap-0.5">
                            {s.review.result ? <Status status={s.review.result === 'MATCHES' ? 'MATCHES' : 'MISMATCH'} /> : s.unitId ? <Status status="PENDING" label="To review" /> : <Status status={s.status} />}
                            {s.flags.length > 0 && <span className="font-mono text-[11px] text-warn">{s.flags.length} flags</span>}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </nav>
              {current && <SlipReview key={`${current.bookId}:${current.slipNo}`} slip={current} operatorId={review.data?.operatorId ?? null} />}
            </div>
          )}
        </QueryState>
      )}
    </ConsolePage>
  )
}

function SlipReview({ slip, operatorId }: { slip: PaperSlip; operatorId: string | null }) {
  const fmt = useFormat()
  const { user } = useAuth()
  const reviewM = useReviewPaperSlip()
  const [mismatch, setMismatch] = useState(false)
  const [correct, setCorrect] = useState(false)
  const lot = useLot(slip.unitId ?? undefined)
  const separation =
    user && (user.id === slip.enteredById || user.id === operatorId) && !canOverrideTwoPerson(user.roles)
      ? 'You typed or ran this paper day: another supervisor reviews it.'
      : null
  const main = slip.typed.lots.find((l) => l.stream === 'MAIN') ?? slip.typed.lots[0]
  const r = slip.reweigh

  return (
    <div className="flex min-w-0 flex-col gap-5">
      {separation && <Banner tone="warn">{separation}</Banner>}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Section title={`Slip ${slip.bookNo} · ${slip.slipNo}`} eyebrow="Page photo" bodyClassName="p-2">
          <PhotoViewer photo={slip.photo} alt={`Receipt slip ${slip.bookNo}/${slip.slipNo}`} size="inline" className="min-h-[420px]" />
        </Section>
        <Section title="Typed values" eyebrow={<>Typed by <UserName id={slip.enteredById} /> · <When at={slip.enteredAt} /></>}>
          {!slip.unitId ? (
            <p className="text-[14px] text-muted">This slip is {humanize(slip.status).toLowerCase()} — no lot.</p>
          ) : (
            <div className="flex flex-col gap-4 text-[14px]">
              <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-3 gap-y-1.5">
                <dt className="text-muted">Farmer</dt>
                <dd>
                  {main?.farmerName ?? '—'} <span className="font-mono text-[12px] text-muted">{main?.rosterNo}</span>
                </dd>
                <dt className="text-muted">Crop</dt>
                <dd>{main?.cropName ?? '—'}</dd>
                <dt className="text-muted">Moisture</dt>
                <dd className="font-mono">{slip.typed.moisture ? `${slip.typed.moisture} %` : 'not written'}</dd>
                <dt className="text-muted">Seal</dt>
                <dd className="font-mono">{slip.typed.seal ?? '—'}</dd>
                <dt className="text-muted">Sample weight</dt>
                <dd className="font-mono">{slip.typed.sampleWeightG !== null ? `${slip.typed.sampleWeightG} g` : '—'}</dd>
                {slip.typed.homeStorage &&
                  Object.entries(slip.typed.homeStorage).map(([k, v]) => (
                    <div key={k} className="contents">
                      <dt className="text-muted">{humanize(k)}</dt>
                      <dd className="font-mono">{String(v)}</dd>
                    </div>
                  ))}
              </dl>
              <table className="w-full text-[13.5px]">
                <caption className="sr-only">Bags on the slip</caption>
                <thead className="font-mono text-[10.5px] uppercase tracking-label text-muted">
                  <tr>
                    <th scope="col" className="text-left">Tag</th>
                    <th scope="col" className="text-left">Sack</th>
                    <th scope="col" className="text-right">Gross</th>
                    <th scope="col" className="text-right">Net</th>
                    <th scope="col" className="text-right">Re-weigh</th>
                  </tr>
                </thead>
                <tbody>
                  {slip.typed.bags.map((b) => (
                    <tr key={b.id} className={cn('border-t border-rule/70', b.status === 'VOID' && 'text-muted line-through')}>
                      <td className="py-1 font-mono">{b.tag ?? '—'}</td>
                      <td className="font-mono">{b.sackType ?? '—'}</td>
                      <td className="text-right"><Kg grams={b.grossG} /></td>
                      <td className="text-right"><Kg grams={b.netG} /></td>
                      <td className="text-right">{r.outOfTolerance.includes(b.id) ? <span className="text-danger">outside</span> : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex flex-wrap gap-3">
                {slip.typed.lots.map((l) => (
                  <Link key={l.id} to={`/c/lots/${l.id}`} className="flex items-center gap-2 rounded-chip border border-rule px-2 py-1 hover:border-ink/40">
                    <span className="font-mono text-[13px]">{l.code}</span>
                    <Stream stream={l.stream} />
                    <Kg grams={l.netG} />
                    {l.hold !== 'NONE' && <Status status={l.hold} />}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </Section>
      </div>

      {slip.unitId && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Section title="Warehouse re-weigh">
            <div className="flex items-center gap-3 text-[14px]">
              <Check ok={r.bagsTotal > 0 && r.bagsReweighed >= r.bagsTotal && r.outOfTolerance.length === 0} />
              <span>
                {fmt.count(r.bagsReweighed)} of {fmt.count(r.bagsTotal)} bags re-weighed
                {r.outOfTolerance.length > 0 && <span className="text-danger"> · {r.outOfTolerance.length} outside tolerance</span>}
              </span>
            </div>
            <p className="mt-2 text-[13px] text-muted">The payment waits until every bag is re-weighed within tolerance.</p>
          </Section>
          <Section title="Flags" eyebrow={`${slip.flags.length} open`}>
            {slip.flags.length === 0 ? (
              <p className="text-[14px] text-muted">No open flags.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {slip.flags.map((fl) => (
                  <li key={fl.id}>
                    <Link to={`/c/exceptions?open=${fl.id}`} className="flex items-center gap-2 text-[14px] underline decoration-rule">
                      <WarningDiamond className="text-warn" aria-hidden="true" /> {exceptionLabel(fl.type).en}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}

      {slip.unitId && (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 rounded-card border border-rule bg-surface px-4 py-3 shadow-pop">
          <span className="mr-auto text-[14px]">
            {slip.review.result ? (
              <>
                Reviewed: <Status status={slip.review.result} /> by <UserName id={slip.review.reviewedById} />
              </>
            ) : (
              'Does the typed slip match the photo?'
            )}
          </span>
          <ActionButton
            perm="units:review-paper"
            leadingIcon={<CheckCircle />}
            blockedReason={separation}
            loading={reviewM.isPending && reviewM.variables?.body.result === 'MATCHES'}
            onClick={() => void reviewM.mutateAsync({ unitId: slip.unitId!, body: { result: 'MATCHES' } }).catch(() => undefined)}
          >
            Matches
          </ActionButton>
          <ActionButton perm="units:review-paper" variant="danger" leadingIcon={<WarningDiamond />} blockedReason={separation} onClick={() => setMismatch(true)}>
            Raise mismatch
          </ActionButton>
          {lot.data && (
            <>
              <HoldActions lot={lot.data} />
              <ActionButton perm="units:correct" variant="secondary" leadingIcon={<Scales />} onClick={() => setCorrect(true)}>
                Correct weight
              </ActionButton>
              <CorrectionDialog open={correct} onClose={() => setCorrect(false)} lot={lot.data} />
            </>
          )}
        </div>
      )}
      <MismatchDialog
        open={mismatch}
        onClose={() => setMismatch(false)}
        loading={reviewM.isPending}
        onSave={(body) =>
          reviewM
            .mutateAsync({ unitId: slip.unitId!, body: { result: 'MISMATCH', ...body } })
            .then(() => setMismatch(false))
            .catch(() => undefined)
        }
      />
    </div>
  )
}

function MismatchDialog({
  open,
  onClose,
  loading,
  onSave,
}: {
  open: boolean
  onClose: () => void
  loading: boolean
  onSave: (b: { field: string; slipValue?: string; typedValue?: string; note?: string }) => void
}) {
  const [field, setField] = useState('')
  const [slipValue, setSlipValue] = useState('')
  const [typedValue, setTypedValue] = useState('')
  const [note, setNote] = useState('')
  const [touched, setTouched] = useState(false)
  const err = touched && !field ? 'A mismatch names the field' : touched && !slipValue.trim() ? 'Write what the slip says' : null
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Raise PAPER_MISMATCH"
      description="The lot stays on hold and its payment waits until the mismatch is settled."
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={loading}
            onClick={() => {
              setTouched(true)
              if (!field || !slipValue.trim()) return
              onSave({ field, slipValue: slipValue.trim(), typedValue: typedValue.trim() || undefined, note: note.trim() || undefined })
            }}
          >
            Raise mismatch
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Field" required error={touched && !field ? 'Required' : null}>
          <Select value={field} onChange={setField} placeholder="Which value differs" options={MISMATCH_FIELDS.map((f) => ({ value: f, label: humanize(f) }))} />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Slip says" required error={err && field ? err : null}>
            <Input value={slipValue} onChange={(e) => setSlipValue(e.target.value)} maxLength={120} />
          </Field>
          <Field label="Typed as">
            <Input value={typedValue} onChange={(e) => setTypedValue(e.target.value)} maxLength={120} />
          </Field>
        </div>
        <Field label="Note">
          <TextArea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={300} />
        </Field>
      </div>
    </Modal>
  )
}
